import ServiceRouteName from '@/views/company-services/routes'
import ServiceRoutes from '@/views/company/service/routes'
import TicketRoutes from '@/views/company/ticket/routes'
import ContactRoutes from '@/views/company/contact/routes'
import LocationRoutes from '@/views/company/location/routes'
import { Permission } from '@/lib/permissions/v1'
import CompanySettingsRoute from '@/views/company/settings/routes'
import CompanySendRoutes from '@/views/company/send/routes'
import SendRoutes from '@/views/company/send/routes'
import CompanyAssetRoutes from '@/views/company/asset/routes'
import CompanySecretRoutes from '@/views/company/secret/routes'
import CompanyLogRoutes from '@/views/company/log/routes'
import CompanyInvoiceRoutes from '@/views/company/invoice/routes'
import CompanyReceiptRoutes from '@/views/company/receipt/routes'
import CompanyStatementRoutes from '@/views/company/statement/routes'
import CompanyPayRoutes from '@/views/company/pay/routes'
import BillingRoutes from '@/views/company/billing/routes'
import { companyParamKey } from '@/utils/param-keys'
import ArticleRoutes from '@/views/company/article/routes'
import { Route } from '@/composables/route'

export enum CompanyRouteName {
    Index = 'customers.index',
    Create = 'companies.create',
    Dashboard = 'customer.dashboard.index',
}

export enum ContactRouteName {
    Index = 'customers.contacts.index',
    Create = 'customers.contacts.create',
    Edit = 'customers.contacts.edit',
    Logs = 'customers.contacts.logs',
    LogDetails = 'customers.contacts.log.details',
    ResetPassword = 'customers.contacts.reset-password',
    Show = 'customers.contacts.show',
}

export enum LocationRouteName {
    Index = 'customers.addresses.index',
    Create = 'customers.addresses.create',
    Edit = 'customers.addresses.edit',
    Logs = 'customers.addresses.logs',
    LogDetails = 'customers.addresses.log.details',
    ResetPassword = 'customers.addresses.reset-password',
    Show = 'customers.addresses.show',
}

export enum AssetRouteName {
    Index = 'customers.assets.index',
    Create = 'customers.assets.create',
    Edit = 'customers.assets.edit',
    Show = 'customers.assets.show',
    Logs = 'customers.assets.logs',
    LogDetails = 'customers.assets.logs.details',
    Secrets = 'customers.assets.secrets',
    SecretDetails = 'customers.assets.secrets.details',
    SecretCreate = 'customers.assets.secrets.create',
    SecretEdit = 'customers.assets.secrets.edit',
    SecretLogs = 'customers.assets.secrets.logs',
    SecretLogDetails = 'customers.assets.secrets.logs.details',
}

export const setRouteQueries = async (route: Route) => {
    await route.addSortItem({
        column: 'name',
        direction: 'asc',
    })

    route.view.value = 'flat'
}
export default {
    path: '/companies',
    component: () => import('@/views/Index.vue'),
    name: 'customers',
    meta: {
        label: 'Companies',
        auth: true,
        location: 'sidebar-top',
        icon: 'fal fa-users fa-fw',
        permission: 'manage-customers',
        group: 'main',
    },
    children: [
        {
            path: 'all',
            name: CompanyRouteName.Index,
            component: () => import('@/views/customer/Index.vue'),
            meta: {
                label: 'Companies',
                auth: true,
                permission: 'manage-customers',
                icon: 'fal fa-users fa-fw',
                location: 'sidebar',
                parent: 'customers',
            },
        },
        ServiceRouteName,
        {
            path: 'contacts',
            name: ContactRouteName.Index,
            component: () => import('@/views/contact/Index.vue'),
            meta: {
                label: 'Contacts',
                title: 'Companies Contacts',
                auth: true,
                permission: 'manage-customer-contacts',
                icon: 'fal fa-address-card fa-fw',
                location: 'sidebar',
                parent: 'customers',
            },
            children: [
                {
                    path: 'create',
                    name: ContactRouteName.Create,
                    component: () => import('@/views/contact/Index.vue'),
                    meta: {
                        title: 'Create Contact',
                        auth: true,
                        permission: 'manage-customer-contacts',
                        icon: 'fal fa-address-card fa-fw',
                        location: 'sidebar',
                        parent: 'customers.contacts.index',
                    },
                },
                {
                    path: ':contactUuid/edit',
                    name: ContactRouteName.Edit,
                    component: () => import('@/views/contact/Index.vue'),
                    meta: {
                        title: 'Edit Contact',
                        auth: true,
                        permission: 'manage-customer-contacts',
                        icon: 'fal fa-address-card fa-fw',
                        location: 'sidebar',
                        parent: 'customers.contacts.index',
                    },
                },
                {
                    path: ':contactUuid/logs',
                    name: ContactRouteName.Logs,
                    component: () => import('@/views/contact/Index.vue'),
                    meta: {
                        label: 'Contact Logs',
                        auth: true,
                        permission: Permission.EventViewAny,
                    },
                    children: [
                        {
                            path: ':logId',
                            name: ContactRouteName.LogDetails,
                            component: () => import('@/views/contact/Index.vue'),
                            meta: {
                                label: 'Log Details',
                                auth: true,
                                permission: Permission.EventView,
                            },
                        },
                    ],
                },
                {
                    path: ':contactUuid/reset-password',
                    name: ContactRouteName.ResetPassword,
                    component: () => import('@/views/contact/Index.vue'),
                    meta: {
                        label: 'Reset Contact Password',
                        auth: true,
                        permission: Permission.CompanyContactUpdate,
                    },
                },
                {
                    path: ':contactUuid/contact-details',
                    name: ContactRouteName.Show,
                    component: () => import('@/views/contact/Index.vue'),
                    meta: {
                        label: 'Contact Details',
                        auth: true,
                        permission: Permission.CompanyContactView,
                    },
                },
            ],
        },
        {
            path: 'addresses',
            name: LocationRouteName.Index,
            component: () => import('@/views/address/Index.vue'),
            meta: {
                label: 'Addresses',
                title: 'Companies Addresses',
                auth: true,
                permission: 'manage-customer-addresses',
                icon: 'fal fa-location fa-fw',
                location: 'sidebar',
                parent: 'customers',
            },
            children: [
                {
                    path: 'create',
                    name: LocationRouteName.Create,
                    component: () => import('@/views/address/Index.vue'),
                    meta: {
                        label: 'Create Address',
                        auth: true,
                        permission: 'manage-customer-addresses',
                        icon: 'fal fa-location fa-fw',
                        location: 'sidebar',
                        parent: 'customers',
                    },
                },
                {
                    path: ':locationUuid/edit',
                    name: LocationRouteName.Edit,
                    component: () => import('@/views/address/Index.vue'),
                    meta: {
                        label: 'Edit Address',
                        auth: true,
                        permission: 'manage-customer-addresses',
                        icon: 'fal fa-location fa-fw',
                        location: 'sidebar',
                        parent: 'customers',
                    },
                },
                {
                    path: ':locationUuid/logs',
                    name: LocationRouteName.Logs,
                    component: () => import('@/views/address/Index.vue'),
                    meta: {
                        label: 'Location Logs',
                        auth: true,
                        permission: Permission.EventViewAny,
                    },
                    children: [
                        {
                            path: ':logId',
                            name: LocationRouteName.LogDetails,
                            component: () => import('@/views/address/Index.vue'),
                            meta: {
                                label: 'Log Details',
                                auth: true,
                                permission: Permission.EventViewAny,
                            },
                        },
                    ],
                },
            ],
        },
        {
            path: 'assets',
            name: AssetRouteName.Index,
            component: () => import('@/views/company-assets/Index.vue'),
            meta: {
                auth: true,
                label: 'Assets',
                title: 'Companies Assets',
                icon: 'fal fa-sack-dollar fa-fw',
                location: 'sidebar',
                parent: 'customers',
                permission: 'manage-customer-assets',
            },
            children: [
                {
                    path: ':assetId/details',
                    name: 'customer-assets.asset.details',
                    component: () => import('@/views/company-assets/Index.vue'),
                    meta: {
                        auth: true,
                        label: 'Company Asset Details',
                        icon: 'fal fa-sack-dollar fa-fw',
                        group: 'main',
                        location: 'sidebar',
                        permission: 'manage-customer-assets',
                        parent: 'customers.assets.index',
                    },
                },
                {
                    path: 'create',
                    name: AssetRouteName.Create,
                    component: () => import('@/views/company-assets/Index.vue'),
                    meta: {
                        label: 'Create',
                        title: 'Create Asset',
                        auth: true,
                        permission: 'manage-assets',
                        location: null,
                        parent: 'assets',
                    },
                },
                {
                    path: ':assetUuid/edit',
                    name: AssetRouteName.Edit,
                    component: () => import('@/views/company-assets/Index.vue'),
                    meta: {
                        label: 'Edit Asset',
                        auth: true,
                        permission: 'manage-customer-assets',
                        icon: 'fal fa-location fa-fw',
                        location: 'sidebar',
                        parent: 'assets',
                    },
                },
                {
                    path: ':assetUuid/details',
                    name: AssetRouteName.Show,
                    component: () => import('@/views/company-assets/Index.vue'),
                    meta: {
                        label: 'Asset Details',
                        auth: true,
                        permission: 'manage-customer-assets',
                        icon: 'fal fa-location fa-fw',
                        location: 'sidebar',
                        parent: 'assets',
                    },
                },
                {
                    path: ':assetUuid/logs',
                    name: AssetRouteName.Logs,
                    component: () => import('@/views/company-assets/Index.vue'),
                    meta: {
                        label: 'Asset Logs',
                        auth: true,
                        permission: Permission.EventViewAny,
                    },
                    children: [
                        {
                            path: ':logId',
                            name: AssetRouteName.LogDetails,
                            component: () => import('@/views/company-assets/Index.vue'),
                            meta: {
                                label: 'Log Details',
                                auth: true,
                                permission: Permission.EventViewAny,
                            },
                        },
                    ],
                },
                {
                    path: ':assetUuid/secrets',
                    name: AssetRouteName.Secrets,
                    component: () => import('@/views/company-assets/Index.vue'),
                    meta: {
                        label: 'Asset Secrets',
                        auth: true,
                        permission: Permission.SecretViewAny,
                    },
                    children: [
                        {
                            path: ':secretUuid',
                            name: AssetRouteName.SecretDetails,
                            component: () => import('@/views/company-assets/Index.vue'),
                            meta: {
                                label: 'Secret Details',
                                auth: true,
                                permission: Permission.SecretView,
                            },
                        },
                        {
                            path: 'create',
                            name: AssetRouteName.SecretCreate,
                            component: () => import('@/views/company-assets/Index.vue'),
                            meta: {
                                label: 'Create Company Asset Secret',
                                auth: true,
                                permission: Permission.SecretCreate,
                            },
                        },
                        {
                            path: ':secretUuid/edit',
                            name: AssetRouteName.SecretEdit,
                            component: () => import('@/views/company-assets/Index.vue'),
                            meta: {
                                label: 'Edit Company Asset Secret',
                                auth: true,
                                permission: Permission.SecretUpdate,
                            },
                        },
                        {
                            path: ':secretUuid/logs',
                            name: AssetRouteName.SecretLogs,
                            component: () => import('@/views/company-assets/Index.vue'),
                            meta: {
                                label: 'Secret Logs',
                                auth: true,
                                permission: Permission.EventViewAny,
                            },
                            children: [
                                {
                                    path: ':logId/details',
                                    name: AssetRouteName.SecretLogDetails,
                                    component: () => import('@/views/company/asset/Index.vue'),
                                    meta: {
                                        label: 'Log Details',
                                        auth: true,
                                        permission: Permission.EventView,
                                    },
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            path: 'create',
            name: CompanyRouteName.Create,
            component: () => import('@/views/customer/Index.vue'),
            meta: {
                label: 'Create',
                title: 'Create Customer',
                auth: true,
                permission: 'manage-customers',
                location: null,
                parent: 'customers',
            },
        },
        {
            path: `:${companyParamKey}`,
            component: () => import('@/views/company/Index.vue'),
            meta: {
                label: 'Company',
                auth: true,
                permission: Permission.CompanyView,
            },
            children: [
                {
                    path: 'dashboard',
                    name: CompanyRouteName.Dashboard,
                    component: () => import('@/views/company/dashboard/Index.vue'),
                    meta: {
                        label: 'Dashboard',
                        auth: true,
                        permission: Permission.CompanyView,
                    },
                },
                BillingRoutes,
                ArticleRoutes,
                CompanySettingsRoute,
                CompanySendRoutes,
                CompanyAssetRoutes,
                CompanyLogRoutes,
                CompanyPayRoutes,
                CompanySecretRoutes,
                CompanyInvoiceRoutes,
                CompanyReceiptRoutes,
                CompanyStatementRoutes,
                ContactRoutes,
                LocationRoutes,
                SendRoutes,
                ServiceRoutes,
                TicketRoutes,
            ],
        },
        {
            path: ':customerId?/contacts',
            name: 'customer.contacts.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Contacts',
                auth: true,
                permission: 'manage-customer-contacts',
                location: null,
                parent: 'customers',
            },
            children: [
                {
                    path: 'create',
                    name: 'customer.contacts.create',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Create Customer Contact',
                        auth: true,
                        permission: 'manage-customer-contacts',
                        location: null,
                        parent: 'customers',
                    },
                },
                {
                    path: ':contactId/edit',
                    name: 'customer.contacts.edit',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Edit Customer Contact',
                        auth: true,
                        permission: 'manage-customer-contacts',
                        location: null,
                        parent: 'customers',
                    },
                },
                {
                    path: ':contactId/reset-password',
                    name: 'customer.contacts.reset-password',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Reset Company Contact Password',
                        auth: true,
                        permission: 'manage-customer-contacts',
                        location: null,
                        parent: 'customers',
                    },
                },
                {
                    path: ':contactId',
                    name: 'customer.contacts.show',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Customer Contact',
                        auth: true,
                        permission: 'manage-customer-contacts',
                        location: null,
                        parent: 'customers',
                    },
                },
            ],
        },
        {
            path: ':customerId?/sites',
            name: 'customer.sites.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Sites',
                auth: true,
                permission: 'manage-customer-sites',
                location: null,
                parent: 'customers',
            },
            children: [
                {
                    path: 'create',
                    name: 'customer.sites.create',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Create Site',
                        auth: true,
                        permission: 'manage-customer-sites',
                        location: null,
                        parent: 'customers',
                    },
                },
                {
                    path: ':siteId/edit',
                    name: 'customer.sites.edit',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Edit Site',
                        auth: true,
                        permission: 'manage-customer-sites',
                        location: null,
                        parent: 'customers',
                    },
                },
            ],
        },
        {
            path: ':customerId?/addresses',
            name: 'customer.addresses.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Addresses',
                auth: true,
                permission: 'manage-customers',
                location: null,
                parent: 'customers',
            },
        },
        {
            path: ':customerId?/finance',
            name: 'customer.finance.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Finance',
                auth: true,
                permission: 'manage-customers',
                location: null,
                parent: 'customers',
            },
        },
        {
            path: ':customerId?/statements',
            name: 'customer.statements.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Statements',
                auth: true,
                permission: 'manage-customer-payments',
                location: null,
                parent: 'customers',
            },
        },
        {
            path: ':customerId?/accountposition',
            name: 'customer.accountposition.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Account Position',
                auth: true,
                permission: 'manage-customer-payments',
                location: null,
                parent: 'customers',
            },
        },
        {
            path: ':customerId?/balancefix',
            name: 'customer.balancefix.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Balance Fix',
                auth: true,
                permission: 'manage-customer-payments',
                location: null,
                parent: 'customers',
            },
        },
        {
            path: ':customerId?/overdue',
            name: 'customer.overdue.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Overdue',
                auth: true,
                permission: 'manage-customer-payments',
                location: null,
                parent: 'customers',
            },
        },
        {
            path: ':customerId?/invoices',
            name: 'customer.invoices.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Invoices',
                auth: true,
                permission: Permission.InvoiceViewAny,
                location: null,
                parent: 'customers',
            },
            children: [
                {
                    path: ':invoiceId',
                    name: 'customer.invoices.details',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Invoice Details',
                        auth: true,
                        permission: Permission.InvoiceView,
                        location: null,
                        parent: 'customer.invoices.index',
                    },
                    children: [
                        {
                            path: 'items/:itemId/edit',
                            name: 'customer.invoices.items.edit',
                            component: () => import('@/views/customer/dashboard/Index.vue'),
                            meta: {
                                label: 'Edit Invoice Item',
                                auth: true,
                                permission: Permission.InvoiceUpdate,
                                location: null,
                                parent: 'customer.invoices.details',
                            },
                        },
                    ],
                },
                {
                    path: ':invoiceId/edit',
                    name: 'customer.invoices.edit',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Edit Invoice',
                        auth: true,
                        permission: Permission.InvoiceUpdate,
                        parent: 'customer.invoices.index',
                    },
                },
                {
                    path: ':invoiceId/history',
                    name: 'customer.invoices.history',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Invoice History',
                        auth: true,
                        permission: 'invoice:view',
                        parent: 'customer.invoices.index',
                    },
                    children: [
                        {
                            path: ':historyId/details',
                            name: 'customer.invoices.history.details',
                            component: () => import('@/views/customer/dashboard/Index.vue'),
                            meta: {
                                label: 'Invoice History Details',
                                auth: true,
                                permission: 'invoice:view',
                                parent: 'customer.invoices.index',
                            },
                        },
                    ],
                },
            ],
        },
        {
            path: ':customerId?/receipts',
            name: 'customer.receipts.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Receipts',
                auth: true,
                permission: 'manage-customer-payments',
                location: null,
                parent: 'customers',
            },
            children: [
                {
                    path: ':paymentId/edit',
                    name: 'customer.receipts.edit',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Edit Receipts',
                        auth: true,
                        permission: 'manage-customer-payments',
                        location: null,
                        parent: 'customer.receipts.index',
                    },
                },
            ],
        },
        {
            path: ':customerId?/parameters',
            name: 'customer.parameters.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Parameters',
                auth: true,
                permission: 'manage-customers',
                location: null,
                parent: 'customers',
            },
        },
        {
            path: ':customerId?/payments',
            name: 'customer.payments.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Payments',
                auth: true,
                permission: 'manage-customers',
                location: null,
                parent: 'customers',
            },
        },
        {
            path: ':customerId?/payment-methods',
            name: 'customer.paymentMethods.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Payment Methods',
                auth: true,
                permission: 'manage-customers',
                location: null,
                parent: 'customers',
            },
        },
        {
            path: ':customerId?/linked-accounts',
            name: 'customer.linkedAccounts.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Linked Accounts',
                auth: true,
                permission: 'manage-customers',
                location: null,
                parent: 'customers',
            },
        },
        {
            path: ':customerId?/billing',
            name: 'customer.billing.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Billing',
                auth: true,
                permission: 'manage-customer-billing',
                location: null,
                parent: 'customers',
            },
            children: [
                {
                    path: 'multiple',
                    name: 'customer.billing.multiple.index',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Multi Service Bill',
                        auth: true,
                        permission: 'manage-customer-billing',
                        location: null,
                        parent: 'customers',
                    },
                    children: [
                        {
                            path: ':itemId/edit',
                            name: 'customer.billing.multiple.items.edit',
                            component: () => import('@/views/customer/dashboard/Index.vue'),
                            meta: {
                                label: 'Edit Invoice Item',
                                auth: true,
                                permission: 'manage-customer-billing',
                                location: null,
                                parent: 'customer.billing.multiple.index',
                            },
                        },
                    ],
                },
                {
                    path: 'single',
                    name: 'customer.billing.single.index',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Single Service Bill',
                        auth: true,
                        permission: 'manage-customer-billing',
                        location: null,
                        parent: 'customers',
                    },
                },
                {
                    path: 'combine',
                    name: 'customer.billing.combine.index',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Invoice Combine',
                        auth: true,
                        permission: 'manage-customer-billing',
                        location: null,
                        parent: 'customers',
                    },
                    children: [
                        {
                            path: ':itemId/edit',
                            name: 'customer.billing.combine.edit',
                            component: () => import('@/views/customer/dashboard/Index.vue'),
                            meta: {
                                label: 'Edit Invoice Item',
                                auth: true,
                                permission: 'manage-customer-billing',
                                location: null,
                                parent: 'customer.billing.combine.index',
                            },
                        },
                    ],
                },
                {
                    path: 'product',
                    name: 'customer.billing.product.index',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Product Billing',
                        auth: true,
                        permission: 'manage-customer-billing',
                        location: null,
                        parent: 'customers',
                    },
                },
            ],
        },
        {
            path: ':customerId?/tickets',
            name: 'customer.tickets.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Tickets',
                auth: true,
                permission: 'manage-tickets',
                location: null,
                parent: 'customers',
            },
        },
        {
            path: ':customerId?/logs',
            name: 'customer.logs.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Logs',
                auth: true,
                permission: 'manage-customer-logs',
                location: null,
                parent: 'customers',
            },
            children: [
                {
                    path: ':logId',
                    name: 'customer.logs.show',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Log Details',
                        auth: true,
                        permission: 'manage-customer-logs',
                        location: null,
                        parent: 'customer.logs.index',
                    },
                },
            ],
        },
        {
            path: ':customerId?/articles',
            name: 'customer.articles.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Articles',
                auth: true,
                permission: 'manage-articles',
                location: null,
                parent: 'customers',
            },
            children: [
                {
                    path: 'create',
                    name: 'customer.articles.create',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Create',
                        title: 'Create Article',
                        auth: true,
                        permission: 'manage-articles',
                        location: null,
                        parent: 'customer.articles.index',
                    },
                },
                {
                    path: ':articleId/edit',
                    name: 'customer.articles.edit',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Edit',
                        title: 'Edit Article',
                        auth: true,
                        permission: 'manage-articles',
                        location: null,
                        parent: 'customer.articles.index',
                    },
                },
            ],
        },
        {
            path: ':customerId?/links',
            name: 'customer.links.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Links',
                auth: true,
                permission: 'manage-links',
                location: null,
                parent: 'customers',
            },
            children: [
                {
                    path: 'create',
                    name: 'customer.links.create',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Create Link',
                        auth: true,
                        permission: 'manage-links',
                        location: null,
                        parent: 'customers',
                    },
                },
                {
                    path: ':linkId/edit',
                    name: 'customer.links.edit',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Edit Link',
                        auth: true,
                        permission: 'manage-links',
                        location: null,
                        parent: 'customers',
                    },
                },
            ],
        },
        {
            path: ':customerId?/embed',
            name: 'customer.embed.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Embed',
                auth: true,
                permission: 'manage-links',
                location: null,
                parent: 'customers',
            },
        },
        {
            path: ':customerId?/assets',
            name: 'customer.assets.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Assets',
                auth: true,
                permission: 'manage-customer-assets',
                location: null,
                parent: 'customers',
            },
            children: [
                {
                    path: 'create',
                    name: 'customer.assets.create',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Create',
                        title: 'Create Asset',
                        auth: true,
                        permission: 'manage-customer-assets',
                        location: null,
                        parent: 'customer.assets.index',
                    },
                },
                {
                    path: ':assetId/edit',
                    name: 'customer.assets.edit',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Edit',
                        title: 'Edit Asset',
                        auth: true,
                        permission: 'manage-customer-assets',
                        location: null,
                        parent: 'customer.assets.index',
                    },
                },
                {
                    path: ':assetId/details',
                    name: 'customer.assets.details',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Edit',
                        title: 'Asset Details',
                        auth: true,
                        permission: 'manage-customer-assets',
                        location: null,
                        parent: 'customer.assets.index',
                    },
                },
                {
                    path: ':assetId/secrets',
                    name: 'customer.assets.secrets',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Secrets',
                        title: 'Asset Secrets',
                        auth: true,
                        permission: 'manage-customer-assets',
                        location: null,
                        parent: 'customer.assets.index',
                    },
                    children: [
                        {
                            path: 'create',
                            name: 'customer.assets.secrets.create',
                            component: () => import('@/views/customer/dashboard/Index.vue'),
                            meta: {
                                label: 'Create Secret',
                                auth: true,
                                permission: 'manage-customer-assets',
                                location: null,
                                parent: 'customer.assets.secrets',
                            },
                        },
                        {
                            path: ':secretId/edit',
                            name: 'customer.assets.secrets.edit',
                            component: () => import('@/views/customer/dashboard/Index.vue'),
                            meta: {
                                label: 'Edit Secret',
                                auth: true,
                                permission: 'manage-customer-assets',
                                location: null,
                                parent: 'customer.assets.secrets',
                            },
                        },
                        {
                            path: ':secretId/details',
                            name: 'customer.assets.secrets.details',
                            component: () => import('@/views/customer/dashboard/Index.vue'),
                            meta: {
                                label: 'Secret Details',
                                auth: true,
                                permission: 'manage-customer-assets',
                                location: null,
                                parent: 'customer.assets.secrets',
                            },
                        },
                    ],
                },
            ],
        },
        {
            path: ':customerId?/secrets',
            name: 'customer.secrets.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Secrets',
                auth: true,
                permission: 'manage-secrets',
                location: null,
                parent: 'customers',
            },
            children: [
                {
                    path: 'create',
                    name: 'customer.secrets.create',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Create Secret',
                        auth: true,
                        permission: 'manage-secrets',
                        location: null,
                        parent: 'customers',
                    },
                },
                {
                    path: ':secretId/edit',
                    name: 'customer.secrets.edit',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Edit Secret',
                        auth: true,
                        permission: 'manage-secrets',
                        location: null,
                        parent: 'customers',
                    },
                },
                {
                    path: ':secretId/details',
                    name: 'customer.secrets.details',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Secret Details',
                        auth: true,
                        permission: 'manage-secrets',
                        location: null,
                        parent: 'customers',
                    },
                },
            ],
        },
        {
            path: ':customerId?/emails',
            name: 'customer.emails.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Emails',
                auth: true,
                permission: 'manage-customer-emails',
                location: null,
                parent: 'customers',
            },
            children: [
                {
                    path: ':emailId/show',
                    name: 'customer.emails.show',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Email Details',
                        auth: true,
                        permission: 'manage-customer-emails',
                        location: null,
                        parent: 'customer.emails.index',
                    },
                },
            ],
        },
        {
            path: ':customerId?/media',
            name: 'customer.media.index',
            component: () => import('@/views/customer/dashboard/Index.vue'),
            meta: {
                label: 'Media',
                auth: true,
                permission: 'manage-customer-media',
                location: null,
                parent: 'customers',
            },
            children: [
                {
                    path: 'create',
                    name: 'customer.media.create',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Create',
                        title: 'Create Media',
                        auth: true,
                        permission: 'manage-customer-media',
                        location: null,
                        parent: 'customer.media.index',
                    },
                },
                {
                    path: ':mediaId/edit',
                    name: 'customer.media.edit',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Edit',
                        title: 'Edit Media',
                        auth: true,
                        permission: 'manage-customer-media',
                        location: null,
                        parent: 'customer.media.index',
                    },
                },
                {
                    path: ':mediaId/files',
                    name: 'customer.media.files',
                    component: () => import('@/views/customer/dashboard/Index.vue'),
                    meta: {
                        label: 'Files',
                        title: 'Files',
                        auth: true,
                        permission: 'manage-customer-media',
                        location: null,
                        parent: 'customer.media.index',
                    },
                },
            ],
        },
    ],
}
